/**
 * Initial Prompt Library (spec section 13).
 *
 * Every prompt is written for a *loop source*, not a scene: locked or
 * minimally moving camera, continuous environmental motion, no cuts,
 * no dialogue, no text. The Prompt Generator appends the technical
 * footer (duration / aspect ratio / resolution) at copy time, so these
 * bodies stay provider-agnostic.
 */

/** Baseline negative prompt shared by every entry. */
export const DEFAULT_NEGATIVE_PROMPT = [
  'people talking',
  'lip movement',
  'mouth movement',
  'speech',
  'text',
  'subtitles',
  'captions',
  'logos',
  'brand marks',
  'watermark',
  'signature',
  'scene cut',
  'hard cut',
  'montage',
  'fast camera whip',
  'shaky handheld jitter',
  'zoom blur',
  'lens flare artefacts',
  'large lighting change',
  'objects appearing or disappearing',
  'crowd in focus',
  'deformed hands',
  'extra limbs',
  'low resolution',
  'compression artefacts',
  'cartoon',
  'anime',
  'illustration',
  '3d render look',
].join(', ');

const PROVIDERS_ALL = ['google-veo', 'google-vids', 'generic'];

/** Compact factory so 24 entries stay readable and consistent. */
function prompt(id, sceneId, overrides) {
  return {
    id,
    sceneId,
    prompt: '',
    negativePrompt: DEFAULT_NEGATIVE_PROMPT,
    duration: 8,
    aspectRatio: '9:16',
    style: 'cinematic-realistic',
    providerCompatibility: PROVIDERS_ALL,
    enabled: true,
    tags: [],
    ...overrides,
  };
}

export const PROMPTS = [
  // ---------------------------------------------------------------- Taxi Night
  prompt('taxi-night-cinematic', 'taxi-night', {
    duration: 10,
    tags: ['cinematic', 'headline'],
    prompt:
      'Interior of a moving taxi at night, shot from the passenger side. The camera is locked to the cabin and moves only with the car suspension. Headlights and shop signs slide continuously across the side windows, leaving soft light trails. Rain-flecked glass with subtle interior reflections. The driver is out of frame. Steady ambient city motion, no cuts, no people in focus.',
  }),
  prompt('taxi-night-rain', 'taxi-night', {
    duration: 10,
    style: 'moody-noir',
    tags: ['rain', 'moody'],
    prompt:
      'Rainy night inside a moving taxi. Water droplets cling to the side window and slowly travel along the glass while city lights smear into soft bokeh behind them. Camera locked to the cabin, gentle suspension sway only. Warm dashboard glow at the bottom of frame. Continuous, uninterrupted environmental motion with no cuts and no people in frame.',
  }),
  prompt('taxi-night-urban', 'taxi-night', {
    duration: 8,
    style: 'documentary',
    tags: ['urban', 'documentary'],
    prompt:
      'Handheld but stable view from the back seat of a city taxi at night. Street lamps pass the windows in a regular rhythm, other vehicles drift past out of focus, occasional pedestrian silhouette on the pavement stays blurred. Camera stays inside the car with only small natural movement. Continuous rolling motion, no cuts, no dialogue.',
  }),
  prompt('taxi-night-tehran', 'taxi-night', {
    duration: 10,
    style: 'cinematic-realistic',
    tags: ['regional', 'urban'],
    prompt:
      'Night taxi interior in a dense Middle Eastern metropolis. Warm sodium street lighting, illuminated shopfronts and a distant tower silhouette slide past the side windows. Camera fixed inside the cabin with suspension-only movement, faint interior reflections on the glass. Continuous ambient city motion, no text, no signage legible, no cuts, no people in focus.',
  }),
  prompt('taxi-night-minimal', 'taxi-night', {
    duration: 8,
    style: 'clean-minimal',
    tags: ['minimal', 'low-motion'],
    prompt:
      'Minimal night taxi interior: mostly dark cabin, one narrow band of passing city light moving slowly across the window, faint rain texture on the glass. Very low motion, camera locked, no visible passengers. Clean and quiet, loop-friendly, no cuts, no text.',
  }),

  // --------------------------------------------------------------------- Metro
  prompt('metro-cinematic', 'metro', {
    duration: 10,
    style: 'documentary',
    tags: ['transit', 'headline'],
    prompt:
      'Inside a metro carriage moving through a tunnel. Ceiling strip lights pass overhead in a steady rhythm while blurred platform lights sweep past the far windows. The carriage sways very slightly. Camera is mounted at window height and does not move beyond that sway. Empty carriage, no passengers in focus, continuous motion, no cuts.',
  }),
  prompt('metro-minimal', 'metro', {
    duration: 8,
    style: 'clean-minimal',
    tags: ['minimal', 'low-motion'],
    prompt:
      'Minimal metro interior: dark carriage, thin ceiling lights travelling overhead, one blurred light band sliding across the window glass. Very slow steady motion, camera locked, no people. Calm and loop-friendly, no cuts, no text, no logos.',
  }),

  // ----------------------------------------------------------------- Cafe Rain
  prompt('cafe-rain-cinematic', 'cafe-rain', {
    duration: 12,
    style: 'analog-film',
    tags: ['rain', 'calm', 'headline'],
    prompt:
      'Quiet cafe table beside a large window on a rainy afternoon. Rain streaks run down the glass, warm interior light falls across a cup and a notebook, blurred street movement beyond. Camera is locked off on the table. Continuous rain and soft out-of-focus traffic, no cuts, no people in frame, no readable text.',
  }),
  prompt('cafe-rain-minimal', 'cafe-rain', {
    duration: 10,
    style: 'clean-minimal',
    tags: ['minimal', 'calm'],
    prompt:
      'Minimal rainy cafe window: shallow depth of field, rain trails on glass, warm bokeh behind, a single cup on the table. Camera completely static. Only the rain and the bokeh drift move. Loop-friendly, no cuts, no people, no text.',
  }),

  // ------------------------------------------------------------------- Car POV
  prompt('car-pov-cinematic', 'car-pov', {
    duration: 10,
    tags: ['driving', 'motion'],
    prompt:
      'Driver point-of-view on an empty road at dusk. Road markings flow steadily toward the camera, dashboard glow sits at the bottom of frame, the horizon stays level with a very gentle float. Consistent forward motion at constant speed, no overtaking, no other vehicles close to camera. No cuts, no text on the dashboard, loop-friendly.',
  }),

  // --------------------------------------------------------------- Gaming Room
  prompt('gaming-room-cinematic', 'gaming-room', {
    duration: 10,
    tags: ['tech', 'room'],
    prompt:
      'Dark gaming room lit only by monitor glow. Soft coloured spill moves slowly across the desk and the wall, dust particles drift through the light beam, a mechanical keyboard sits in soft focus. Camera is locked off. Slow continuous ambient motion, no people, no readable on-screen content, no cuts.',
  }),

  // -------------------------------------------------------------- Pizza Kitchen
  prompt('pizza-kitchen-cinematic', 'pizza-kitchen', {
    duration: 12,
    style: 'documentary',
    tags: ['food', 'warm'],
    prompt:
      'Warm commercial kitchen at night. Oven glow pulses gently, steam drifts upward through the light, stainless steel catches soft reflections. Camera holds a stable close position with only tiny handheld breathing. Only food and equipment in frame, no hands, no faces. Continuous motion, no cuts, no text.',
  }),

  // --------------------------------------------------------------- Rainy Window
  prompt('rainy-window-cinematic', 'rainy-window', {
    duration: 10,
    tags: ['rain', 'headline'],
    prompt:
      'Close-up of rain running down a window at night. Droplets form, merge and travel slowly down the glass while out-of-focus city lights shimmer behind. Camera is completely static with a very slow focus breath. Continuous uninterrupted rain motion, no cuts, no reflections of people, no text.',
  }),
  prompt('rainy-window-minimal', 'rainy-window', {
    duration: 8,
    style: 'clean-minimal',
    tags: ['minimal', 'abstract'],
    prompt:
      'Abstract rainy window at night: large soft bokeh circles behind a glass plane, a few slow droplet trails crossing the frame, deep shadows. Static camera, minimal motion, no recognisable objects, no text, loop-friendly.',
  }),

  // --------------------------------------------------------------------- Train
  prompt('train-cinematic', 'train', {
    duration: 12,
    style: 'analog-film',
    tags: ['travel', 'night'],
    prompt:
      'Long-distance train window at night. Distant town lights sweep past in a smooth continuous line, faint warm interior reflection ghosts over the glass, the carriage sways in a slow rhythm. Camera is locked to the window frame. No passengers in focus, continuous motion, no cuts, no text.',
  }),

  // ---------------------------------------------------------------- City Night
  prompt('city-night-cinematic', 'city-night', {
    duration: 10,
    tags: ['city', 'headline'],
    prompt:
      'Wide fixed view over a city at night. Slow traffic light streaks move along the road far below, individual windows flicker on and off at random, gentle haze glows around every light source. Camera is completely static on a tripod. Continuous low-level city motion, no camera move, no cuts, no text or signage legible.',
  }),
  prompt('city-night-minimal', 'city-night', {
    duration: 8,
    style: 'clean-minimal',
    tags: ['minimal', 'abstract'],
    prompt:
      'Minimal abstract city at night: dark silhouette of buildings, a few soft warm light points, one slow light trail crossing the lower frame. Almost no motion, static camera, high contrast, no detail, no text, loop-friendly.',
  }),

  // ------------------------------------------------------------------- Bedroom
  prompt('bedroom-cinematic', 'bedroom', {
    duration: 12,
    style: 'analog-film',
    tags: ['indoor', 'calm'],
    prompt:
      'Dim bedroom at night with a single lamp on. A light curtain moves very slightly with the open window, soft shadow drifts on the wall, the bed edge sits at the bottom of frame. Camera is locked off. Gentle continuous air movement, no people, no cuts, no text.',
  }),

  // -------------------------------------------------------------------- Office
  prompt('office-cinematic', 'office', {
    duration: 10,
    style: 'clean-minimal',
    tags: ['work', 'tech', 'headline'],
    prompt:
      'Late-night office desk seen from a low fixed angle. Monitor glow spills across scattered notes and a keyboard, city lights sit far behind a glass wall out of focus, dust drifts in the desk lamp beam. Camera locked. Slow ambient motion only, no people, no readable screen content, no cuts.',
  }),
  prompt('office-minimal', 'office', {
    duration: 8,
    style: 'clean-minimal',
    tags: ['minimal', 'low-motion'],
    prompt:
      'Minimal night office desk: one monitor glow, deep shadow, a thin band of out-of-focus city light behind glass. Very low motion, static camera, no people, no text, loop-friendly.',
  }),

  // ------------------------------------------------------------------- Rooftop
  prompt('rooftop-cinematic', 'rooftop', {
    duration: 12,
    tags: ['city', 'wide'],
    prompt:
      'Rooftop edge at night overlooking a city. Slow clouds drift across the sky, an antenna silhouette sits still against them, distant lights shimmer faintly, a loose cable sways gently in the wind. Camera pans almost imperceptibly. Continuous environmental motion, no people, no cuts, no text.',
  }),

  // ----------------------------------------------------------------------- Bus
  prompt('bus-cinematic', 'bus', {
    duration: 10,
    style: 'documentary',
    tags: ['transit', 'night'],
    prompt:
      'Inside a city bus at night. Interior strip lighting glows steadily while street lights slide across the windows in a smooth rhythm, the suspension moves the frame gently. Empty seats in soft focus. Camera fixed at window height, continuous rolling motion, no passengers in focus, no cuts, no text.',
  }),

  // ------------------------------------------------------------------- Airport
  prompt('airport-cinematic', 'airport', {
    duration: 12,
    style: 'clean-minimal',
    tags: ['travel', 'glass'],
    prompt:
      'Airport terminal at night. A long glass wall reflects the polished floor, runway lights blink slowly in the distance, anonymous silhouettes drift far out of focus. Camera is completely static. Continuous gentle ambient motion, no faces, no readable signage, no cuts.',
  }),

  // -------------------------------------------------------------- Night Street
  prompt('night-street-cinematic', 'night-street', {
    duration: 10,
    style: 'moody-noir',
    tags: ['street', 'rain', 'headline'],
    prompt:
      'Quiet city street after rain at night. Wet asphalt mirrors coloured shop signs, a single distant car passes slowly, steam drifts from a vent, reflections ripple faintly in the puddles. Camera pans very slowly along the street. Continuous ambient motion, no people in focus, no legible text, no cuts.',
  }),

  // ------------------------------------------------------------- Abstract Tech
  prompt('abstract-tech-cinematic', 'abstract-tech', {
    duration: 10,
    style: 'clean-minimal',
    tags: ['abstract', 'data'],
    prompt:
      'Non-representational technology motion: thin flowing data lines travel across a dark field, soft particles drift slowly, a subtle perspective grid pulses in the background. No recognisable objects, no letters, no symbols that read as text. Camera completely static, continuous uniform motion, perfectly loop-friendly.',
  }),
];

export function getPromptById(id, prompts = PROMPTS) {
  return prompts.find((item) => item.id === id) ?? null;
}

export function getPromptsForScene(sceneId, prompts = PROMPTS) {
  return prompts.filter((item) => item.sceneId === sceneId);
}

export function clonePrompt(item) {
  return {
    ...item,
    tags: [...item.tags],
    providerCompatibility: [...item.providerCompatibility],
  };
}

export function allPrompts() {
  return PROMPTS.map(clonePrompt);
}
